import { DirectionalLight, HemisphereLight, PointLight, AmbientLight, Scene, PMREMGenerator } from "three";
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import type { DirectionalLightConfig, HemisphereLightConfig, PointLightConfig, AmbientLightConfig, LightingConfig } from './type';
import type Renderer from "../renderer/renderer";

/**
 * @description 라이트 설정 배열에 따라 모든 라이트를 생성하고 씬에 추가하는 함수
 * @param configs - 라이트 설정 배열
 * @param scene - 씬
 * @param lightsArray - 라이트 객체들을 저장할 배열
 */
export function createLightsFromConfigAndRender(config: LightingConfig, scene: Scene): (DirectionalLight | HemisphereLight | PointLight | AmbientLight)[] {
  const lights: (DirectionalLight | HemisphereLight | PointLight | AmbientLight)[] = [];
  config.lights.forEach(lightConfig => {
    const light = createLightFromConfig(lightConfig);
    lights.push(light);
    scene.add(light);
  });
  return lights;
}

/**
 * @description 라이트 설정에 따라 적절한 라이트 객체를 생성하는 팩토리 함수
 * @param config - 라이트 설정
 * @returns 라이트 객체
 */
export function createLightFromConfig(config: DirectionalLightConfig | HemisphereLightConfig | PointLightConfig | AmbientLightConfig): DirectionalLight | HemisphereLight | PointLight | AmbientLight {
  switch (config.type) {
    case "DirectionalLight":
      return createDirectionalLight(config);
    case "HemisphereLight":
      return createHemisphereLight(config);
    case "PointLight":
      return createPointLight(config);
    case "AmbientLight":
      return createAmbientLight(config);
    default:
      throw new Error(`알 수 없는 조명 타입: ${(config as { type: string }).type}`);
  }
}


/**
 * @description 환경 설정에 따라 적절한 환경 맵을 생성하는 팩토리 함수
 * @param config - 환경 설정
 * @param scene - 씬
 * @param renderer - 렌더러
 */
export function createEnvironmentFromConfig(config: LightingConfig, scene: Scene, renderer: Renderer): void {
  if (!config.environment) {
    return;
  }
  switch (config.environment.loader) {
    case "EXR":
      createEXREnvironment(config, scene, renderer);
      break;
    case "HDR":
      createHDREnvironment(config, scene, renderer);
      break;
    default:
      throw new Error(`알 수 없는 환경 로더 타입: ${config.environment.loader}`);
  }
}

/**
 * EXR 환경 맵 생성 함수
 */
export function createEXREnvironment(config: LightingConfig, scene: Scene, renderer: Renderer): void {
  if (!config.environment) {
    return;
  }
  const loader = new EXRLoader();
  loader.load(config.environment.path, (texture) => {
    const pmremGenerator = new PMREMGenerator(renderer.instance);
    const envMap = pmremGenerator.fromEquirectangular(texture).texture;
    scene.environment = envMap;
    texture.dispose();
    pmremGenerator.dispose();
  });
}

/**
 * HDR 환경 맵 생성 함수
 */
export function createHDREnvironment(config: LightingConfig, scene: Scene, renderer: Renderer): void {
  if (!config.environment) {
    return;
  }
  const loader = new RGBELoader();
  loader.load(config.environment.path, (texture) => {
    const pmremGenerator = new PMREMGenerator(renderer.instance);
    const envMap = pmremGenerator.fromEquirectangular(texture).texture;
    scene.environment = envMap;
    texture.dispose();
    pmremGenerator.dispose();
  });
}

/**
 * DirectionalLight 생성 함수
 */
export function createDirectionalLight(config: DirectionalLightConfig): DirectionalLight {
  const light = new DirectionalLight(config.color, config.intensity);

  if (config.position) {
    light.position.set(config.position.x, config.position.y, config.position.z);
  }

  if (config.castShadow !== undefined) {
    light.castShadow = config.castShadow;
  }

  if (config.frustumCulled !== undefined) {
    light.frustumCulled = config.frustumCulled;
  }

  if (config.shadow) {
    if (config.shadow.autoUpdate !== undefined) {
      light.shadow.autoUpdate = config.shadow.autoUpdate;
    }
    if (config.shadow.radius !== undefined) {
      light.shadow.radius = config.shadow.radius;
    }
    if (config.shadow.mapSize) {
      light.shadow.mapSize.width = config.shadow.mapSize.width;
      light.shadow.mapSize.height = config.shadow.mapSize.height;
    }
    if (config.shadow.bias !== undefined) {
      light.shadow.bias = config.shadow.bias;
    }
    if (config.shadow.normalBias !== undefined) {
      light.shadow.normalBias = config.shadow.normalBias;
    }
  }

  return light;
}

/**
 * HemisphereLight 생성 함수
 */
export function createHemisphereLight(config: HemisphereLightConfig): HemisphereLight {
  const light = new HemisphereLight(config.skyColor, config.groundColor, config.intensity);

  if (config.position) {
    light.position.set(config.position.x, config.position.y, config.position.z);
  }

  if (config.frustumCulled !== undefined) {
    light.frustumCulled = config.frustumCulled;
  }

  return light;
}

/**
 * PointLight 생성 함수
 */
export function createPointLight(config: PointLightConfig): PointLight {
  const light = new PointLight(config.color, config.intensity);

  if (config.position) {
    light.position.set(config.position.x, config.position.y, config.position.z);
  }

  if (config.castShadow !== undefined) {
    light.castShadow = config.castShadow;
  }

  if (config.frustumCulled !== undefined) {
    light.frustumCulled = config.frustumCulled;
  }

  if (config.distance !== undefined) {
    light.distance = config.distance;
  }

  if (config.decay !== undefined) {
    light.decay = config.decay;
  }

  return light;
}

/**
 * AmbientLight 생성 함수
 */
export function createAmbientLight(config: AmbientLightConfig): AmbientLight {
  const light = new AmbientLight(config.color, config.intensity);

  if (config.frustumCulled !== undefined) {
    light.frustumCulled = config.frustumCulled;
  }

  return light;
}
